import React, { useEffect, useState, useRef } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICON_BY_TYPE = {
  follow: "👥",
  like: "❤️",
  comment: "💬",
  new_track: "🎵",
  default: "🔔",
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const markOne = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notif_id}/read`); } catch {}
      setUnread(Math.max(0, unread - 1));
      setNotifs(notifs.map((x) => x.notif_id === n.notif_id ? { ...x, read: true } : x));
    }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    setUnread(0);
    setNotifs(notifs.map((n) => ({ ...n, read: true })));
  };

  return (
    <div ref={ref} className="relative" data-testid="notif-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-white/5 text-white/80 hover:text-white"
        data-testid="notif-bell-btn"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-80 max-h-[500px] overflow-y-auto bg-brand-surface border border-white/10 rounded-2xl shadow-2xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <p className="font-bold text-sm">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-brand-red hover:text-brand-redHover inline-flex items-center gap-1" data-testid="notif-mark-all">
                <CheckCheck size={12} /> Tout marquer lu
              </button>
            )}
          </div>
          {notifs.length === 0 ? (
            <p className="text-sm text-white/50 text-center py-10 px-4">Aucune notification pour l'instant.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {notifs.map((n) => (
                <Link
                  key={n.notif_id}
                  to={n.link || "#"}
                  onClick={() => { markOne(n); setOpen(false); }}
                  className={`flex gap-3 px-4 py-3 hover:bg-white/5 ${n.read ? "opacity-60" : ""}`}
                  data-testid={`notif-${n.notif_id}`}
                >
                  <span className="text-xl shrink-0">{ICON_BY_TYPE[n.type] || ICON_BY_TYPE.default}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{n.title}</p>
                    <p className="text-xs text-white/60 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-white/40 mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-brand-red mt-2 shrink-0" />}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
